import { dispatch as d3dispatch, select, easeLinear } from 'd3c';
import d3wl from 'utils/d3-wl-util';
import _ from 'lodash';
import Sentiment from 'Sentiment';

const dispatch = d3dispatch('mouseover', 'mousemove', 'mouseout', 'click');

export default class GeomapBubbles {
	constructor(options) {
		this.options = options;
		this._state = {
			bubblesShown: true,
			sizeFactor: 1,
			updating: false,
			zoomScale: 1
		};
		this.svgContainer = select(this.options.element).select('svg');
		this.layer = this.svgContainer
			.select('.geomap_zoomer')
			.append('g')
			.attr('class', 'bubbles');
	}

	init() {
		d3wl.rebind(this, dispatch, 'on');
	}

	enable(enable) {
		this._state.bubblesShown = enable;
	}

	update(geomapState, withAnimation, animationCallback) {
		const options = this.options.bubblesConfig;
		const transitionDuration = this.options.animationDuration;
		const _this = this;
		if (!this._state.bubblesShown || !geomapState._data.aggregatedTargets) {
			this.layer.selectAll('.datamaps-bubbleFg').attr('r', 0);
			return;
		}
		const bubbleGroups = this.layer
			.selectAll('g.datamaps-bubbleGroup')
			.data(geomapState._data.aggregatedTargets, d => d.key);

		const updatedGroups = bubbleGroups
			.enter()
			.append('g')
			.attr('class', 'datamaps-bubbleGroup')
			.merge(bubbleGroups)
			.each(function(dataGroup, i) {
				const sortedData = _.sortBy(dataGroup.values, "frequency");
				// Increase bubble radius if bubble stroke overlap with smaller bubble in the same location
				sortedData.forEach((el, index) => {
					if (index > 0) {
						let r1 = el.radius || el.frequency;
						const currentRadius = Math.max((geomapState.radius(r1) / _this._state.zoomScale) * _this._state.sizeFactor, 0);
						const prevBubble = sortedData[index-1];
						const r2 = el.radius || prevBubble.frequency;
						const prevRadius = Math.max((geomapState.radius(r2) / _this._state.zoomScale) * _this._state.sizeFactor, 0);
						if (currentRadius - prevRadius < options.borderWidth / _this._state.zoomScale) {
							r1 = geomapState.radius.invert((prevRadius + options.borderWidth / _this._state.zoomScale) / _this._state.sizeFactor * _this._state.zoomScale);
						}
						sortedData[index] = {
							...el,
							radius: r1
						};
					}
				});
				const bubblesSelection = select(this)
					.selectAll('.datamaps-bubbleFg')
					.data(sortedData, bubbleData => bubbleData.color);
				const mergedB = bubblesSelection
					.enter()
					.append('circle')
					.attr('class', 'datamaps-bubbleFg')
					.attr('id', d => {
						const id = d.id || `${d.latitude}/${d.longitude}`;
						return `datamaps-bubble-${hashCode(id)}`;
					})
					.attr('cx', bubbleData => geomapState.projection([bubbleData.longitude, bubbleData.latitude])[0])
					.attr('cy', bubbleData => geomapState.projection([bubbleData.longitude, bubbleData.latitude])[1])
					.attr('r', 0)
					.merge(bubblesSelection)
					.style('pointer-events', _this._state.updating ? 'none' : null);

				mergedB
					.sort(function(a, b) {
						return b.frequency - a.frequency;
					})
					.each(function(bubbleData, j) {
						const bubble = select(this);
						const r = bubbleData.radius || bubbleData.frequency;
						const fillColor = getBubbleFillColor(geomapState, bubbleData, mergedB.size(), j);

						bubble
							.style('stroke', getBubbleStrokeColor(geomapState, bubbleData, _this._state.updating))
							.style('stroke-width', options.borderWidth / _this._state.zoomScale)
							.style('stroke-opacity', options.borderOpacity)
							.style('fill-opacity', j === mergedB.size() - 1 && mergedB.size() > 1 ? 1 : options.fillOpacity)
							.style('fill', _this._state.updating ? bubbleData.updateColor : fillColor);
						const radius = Math.max((geomapState.radius(r) / _this._state.zoomScale) * _this._state.sizeFactor, 0);
						if (!withAnimation) {
							bubble.attr('r', radius);
						} else {
							const transition = bubble
								.transition()
								.duration(transitionDuration)
								.ease(easeLinear)
								.attr('r', radius);
							// Attach callback only to first bubble
							animationCallback &&
								i === 0 &&
								j === 0 &&
								transition.filter((_t, tIndex) => tIndex === 0).on('end', animationCallback);
						}
					})
					.on('mouseover', function(d) {
						dispatch.call('mouseover', this, d);
					})
					.on('mousemove', function(d) {
						dispatch.call('mousemove', this, d);
					})
					.on('mouseout', function(d) {
						dispatch.call('mouseout', this, d);
					})
					.on('click', function(d) {
						dispatch.call('click', this, d);
					});

				if (!withAnimation) {
					bubblesSelection.exit().remove();
				} else {
					bubblesSelection
						.exit()
						.transition()
						.duration(transitionDuration)
						.attr('r', 0)
						.on('end', function() {
							select(this).remove();
						});
				}
			})
			.style('pointer-events', _this._state.updating ? 'none' : null)
			.sort(function(a, b) {
				return b.frequency - a.frequency;
			});

		if (updatedGroups.size() === 0) {
			setTimeout(animationCallback, transitionDuration);
		}

		if (!withAnimation) {
			bubbleGroups.exit().remove();
		} else {
			bubbleGroups.exit().each(function() {
				select(this)
					.selectAll('.datamaps-bubbleFg')
					.transition()
					.duration(transitionDuration)
					.attr('r', 0)
					.on('end', function() {
						bubbleGroups.exit().remove();
					});
			});
		}
	}

	updateBubbleSize(factor, mapState) {
		this._state.sizeFactor = factor;
		const _this = this;
		this.layer.selectAll('.datamaps-bubbleFg').each(function(d) {
			_this.updateBubbleRadius(d, select(this), mapState);
		});
	}

	zoomed(mapState, currentZoomTransform, isZoomChanged) {
		this._state.zoomScale = currentZoomTransform.k;
		const borderK = this.options.bubblesConfig.borderWidth / this._state.zoomScale;
		if (isZoomChanged && this._state.bubblesShown) {
			const _this = this;
			const gr = this.layer.selectAll('.datamaps-bubbleFg').each(function(d) {
				_this.updateBubbleRadius(d, select(this), mapState);
			});
			gr.style('stroke-width', borderK);
		}
	}

	updateBubbleRadius(d, bubble, mapState) {
		const r = d.radius || d.frequency;
		bubble.attr('r', Math.max((mapState.radius(r) / this._state.zoomScale) * this._state.sizeFactor, 0));
	}

	setUpdatingState(updating) {
		this._state.updating = updating;
	}

	getBounds(mapState) {
		const features = _.map(this.svgContainer.selectAll('.datamaps-bubbleFg').data(), function(d) {
			return [d.longitude, d.latitude];
		});

		return mapState.path.bounds({
			type: 'MultiPoint',
			coordinates: features
		});
	}

	dehighlightBubbles(geomapState) {
		const bubblesConfig = this.options.bubblesConfig;
		this.svgContainer.selectAll('.datamaps-bubbleGroup').each(function() {
			const bubbles = select(this).selectAll('.datamaps-bubbleFg');
			bubbles
				.style('fill', (d, i) => getBubbleFillColor(geomapState, d, bubbles.size(), i))
				.style('stroke', d => getBubbleStrokeColor(geomapState, d))
				.style('fill-opacity', (d, i) =>
					i === bubbles.size() - 1 && bubbles.size() > 1 ? 1 : bubblesConfig.fillOpacity
				)
				.style('stroke-width', bubblesConfig.borderWidth / geomapState.zoomScale);
		});
	}
}

// via http://stackoverflow.com/a/7616484/2266116
const hashCode = s => {
	let hash = 0;
	const len = s.length;
	if (!s || s.length === 0) {
		return hash;
	}
	for (let i = 0; i < len; i++) {
		const chr = s.charCodeAt(i);
		hash = (hash << 5) - hash + chr;
		hash |= 0; // convert to 32bit integer
	}
	return hash;
};

const getBubbleColor = (geomapState, bubbleData) => {
	return geomapState.colorMode === 'sentiment'
		? Sentiment.getColor(bubbleData.sentiment, 'global')
		: bubbleData.color;
};

const getBubbleFillColor = (geomapState, bubbleData, countBubblesInGroup, bubbleIndex) => {
	if (geomapState && geomapState.updating) {
		return bubbleData.updateColor;
	} else {
		return bubbleIndex === countBubblesInGroup - 1 && countBubblesInGroup > 1 ? '#ccc' : getBubbleColor(geomapState, bubbleData);
	}
};

const getBubbleStrokeColor = (geomapState, bubbleData, isUpdating) => {
	if (isUpdating) {
		return bubbleData.updateColor;
	} else {
		return getBubbleColor(geomapState, bubbleData);
	}
};
